import React, { useState, useEffect } from 'react';
// import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { FormField } from '../../../components/Formulaire/FormField';
import FormSection from '../../../components/Formulaire/FormSection';
import { apiService } from '../../../components/Config/api.service';
import { notify } from '../../../components/Utils/Notifications';
import AmountInput from '../../../components/Utils/AmountInput';

const ContractForm = ({ currentContract, employee, refreshContract, onSuccess }) => {
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [contract, setContract] = useState({
        contractName: '',
        employee: '',
        poste: '',
        startDate: false,
        endDate: false,
        contractType: 'CDI',
        status: 'open',
        category: '',
        baseSalary: '',
        convention: '',
        isExecutive: false,
        sursalaire: 0,
        netSalary: 0,
        is_executive: false,
        return_date_leave: false,
        part_ir: false,
        trimf: false,
        manager: false,
        transport_premium: 26000,
    });
    const [categories, setCategories] = useState([]);
    const [conventions, setConventions] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [netAmout, setAmount] = useState(0);

    // États pour le calcul à l'envers
    const [showReverseCalculation, setShowReverseCalculation] = useState(false);
    const [desiredNetSalary, setDesiredNetSalary] = useState('');
    const [reverseCalculationLoading, setReverseCalculationLoading] = useState(false);
    const [calculatedSursalaire, setCalculatedSursalaire] = useState(null);

    //Calcul à l'envers 
    const SIMULATEUR_URL = 'https://backend.senpayor.com/api/payor/submit'



    useEffect(() => {
        if (currentContract?.id) {
            setErrorMessage('');
            setSuccessMessage('');
            apiService.getElementById('/contract', currentContract.id).then(response => {
                const contractData = response.data;
                const category = categories.find(category => category.value === contractData.category);
                const convention = conventions.find(convention => convention.value === category?.conventionType);

                if (category) {
                    contractData.category = category.value;
                    contractData.baseSalary = category.baseSalary;
                    contractData.is_executive = category.is_executive;
                }
                if (convention) {
                    contractData.convention = convention.value;
                }
                setContract(contractData);
                setErrorMessage('');
            }).catch(error => {
                console.error('Error fetching contract:', error);
                setErrorMessage('Erreur lors de la récupération des données du contrat.');
            });
        }
    }, [currentContract, categories, conventions]);

    useEffect(() => {
        apiService.getElement('/conventions')
            .then(response => {
                const conventionOptions = response.data.conventions.map(convention => ({
                    value: convention.id,
                    label: convention.name
                }));
                setConventions(conventionOptions);

                const categoryList = response.data.categories.map(category => ({
                    value: category.id,
                    label: category.name,
                    baseSalary: category.salary_for173h,
                    conventionType: category.convention_type,
                    is_executive: category.is_executive
                }));
                setCategories(categoryList);
            })
            .catch(error => {
                console.error('Error fetching conventions and categories:', error);
                setErrorMessage('Erreur lors de la récupération des conventions et catégories.');
            });
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setContract({
            ...contract,
            [name]: value
        });
    };

    const handleMontantChange = (name, newValue) => {
        setContract(prev => ({ ...prev, [name]: newValue }));
    };

    const handleConventionChange = (selectedOption) => {
        setContract({
            ...contract,
            convention: selectedOption.value,
            category: '',
            baseSalary: ''
        });
        const filtered = categories.filter(category => category.conventionType === selectedOption.value);
        setFilteredCategories(filtered);
    };

    const handleCategoryChange = (selectedOption) => {
        setContract({
            ...contract,
            category: selectedOption.value,
            baseSalary: selectedOption.baseSalary,
            is_executive: selectedOption.is_executive
        });
    };

    // Fonction pour le calcul à l'envers
    const handleReverseCalculation = async () => {
        if (!desiredNetSalary || !contract.convention || !contract.category || !contract.baseSalary) {
            setErrorMessage('Veuillez remplir tous les champs requis (convention, catégorie, salaire de base et salaire net souhaité).');
            return;
        }

        setReverseCalculationLoading(true);
        setErrorMessage('');

        const reversePayload = {
            convention: contract.convention,
            category: contract.category,
            maritalStatus: employee?.maritalStatus || 'single',
            spouseIncome: employee?.spouseIncome || 0,
            numberOfChildren: employee?.numberOfChildren || 0,
            additionalSalary: 0,
            brutSalary: 0,
            baseSalary: contract.baseSalary,
            netAmount: parseFloat(desiredNetSalary),
            taxShares: 1,
            foreign: false,
            executive: contract.is_executive
        };

        try {
            const response = await fetch(SIMULATEUR_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(reversePayload)
            });

            if (!response.ok) {
                throw new Error('Erreur lors du calcul');
            }

            const data = await response.json();

            if (data.result && data.result.success) {
                const calculatedAdditionalSalary = data.result.additional_salary;
                setCalculatedSursalaire(calculatedAdditionalSalary);
            } else {
                setErrorMessage('Erreur dans le calcul. Veuillez vérifier les données saisies.');
            }
        } catch (error) {
            console.error('Error in reverse calculation:', error);
            setErrorMessage('Erreur lors du calcul à l\'envers. Veuillez réessayer.');
        } finally {
            setReverseCalculationLoading(false);
        }
    };

    const copyCalculatedSursalaire = () => {
        if (calculatedSursalaire) {
            setContract(prev => ({ ...prev, sursalaire: calculatedSursalaire }));
            setShowReverseCalculation(false);
            setCalculatedSursalaire(null);
            setDesiredNetSalary('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');

        const contractData = {
            ...contract,
            employee: contract.employee || employee?.id,
        };

        if (currentContract) {
            apiService
                .updateElementById('/contract', currentContract.id, contractData)
                .then((response) => {
                    notify('Contrat mis à jour avec succès !', '', 2);
                    refreshContract();
                    onSuccess('Contrat mis à jour avec succès !');
                })
                .catch((error) => {
                    setErrorMessage('Erreur lors de la mise à jour. Veuillez réessayer.');
                });
        } else {
            apiService
                .saveElement('/contract', contractData)
                .then((response) => {
                    notify('Contrat créé avec succès !');
                    refreshContract();
                    onSuccess('Contrat créé avec succès !');
                })
                .catch((error) => {
                    setErrorMessage('Erreur lors de la création. Veuillez réessayer.');
                });
        }
    };

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            borderColor: state.isFocused ? 'var(--color-primary)' : provided.borderColor,
            boxShadow: state.isFocused ? '0 0 0 1px var(--color-primary)' : provided.boxShadow,
            '&:hover': {
                borderColor: state.isFocused ? 'var(--color-primary)' : provided.borderColor
            },
            borderRadius: '0.375rem',
            padding: '2px',
            minHeight: '42px'
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? 'var(--color-primary)'
                : state.isFocused
                    ? 'rgba(var(--color-primary-rgb), 0.1)'
                    : provided.backgroundColor,
            color: state.isSelected ? 'white' : provided.color
        })
    };

    return (
        <form onSubmit={handleSubmit} className="p-6">
            {errorMessage && <div className="text-center text-red-500 mb-4 p-3 bg-red-50 rounded-lg">{errorMessage}</div>}
            {successMessage && <div className="text-center text-green-500 mb-4 p-3 bg-green-50 rounded-lg">{successMessage}</div>}

            <FormSection title="Informations du contrat" color="blue">
                <FormField label="Référence du contrat:">
                    <input
                        type="text"
                        name="contractName"
                        value={contract.contractName || ''}
                        onChange={handleChange}
                        className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        required
                    />
                </FormField>

                <FormField label="Type de contrat:">
                    <select
                        name="contractType"
                        value={contract.contractType || ''}
                        onChange={handleChange}
                        className="select select-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        required>
                        <option value="CDI">CDI</option>
                        <option value="CDD">CDD</option>
                    </select>
                </FormField>

                <FormField label="Statut:">
                    <select
                        name="status"
                        value={contract.status || ''}
                        onChange={handleChange}
                        className="select select-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        required>
                        <option value="draft">Nouveau</option>
                        <option value="open">En cours</option>
                        <option value="close">Terminé</option>
                        <option value="cancel">Suspendu</option>
                    </select>
                </FormField>
            </FormSection>

            <FormSection title="Informations de l'employé" color="green">
                <FormField label="Employé:">
                    <input
                        type="text"
                        value={employee?.name || ''}
                        className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        readOnly
                    />
                </FormField>

                <FormField label="Poste:">
                    <input
                        type="text"
                        name="poste"
                        value={employee?.poste || ''}
                        onChange={handleChange}
                        className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        readOnly
                    />
                </FormField>

                {currentContract && (
                    <>
                        <FormField label="Part IR:">
                            <input
                                type="text"
                                name="part_ir"
                                value={contract.part_ir || ''}
                                className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                                readOnly
                            />
                        </FormField>

                        <FormField label="TRIMF:">
                            <input
                                type="text"
                                name="trimf"
                                value={contract.trimf || ''}
                                className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                                readOnly
                            />
                        </FormField>
                    </>
                )}
            </FormSection>

            <FormSection title="Période du contrat" color="orange">
                <FormField label="Date de début:">
                    <input
                        type="date"
                        name="startDate"
                        required
                        value={contract.startDate || ''}
                        onChange={handleChange}
                        className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                    />
                </FormField>
                <FormField label="Date de fin:">
                    <input
                        type="date"
                        name="endDate"
                        value={contract.endDate || ''}
                        onChange={handleChange}
                        disabled={contract.contractType === 'CDI'}
                        className={`input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20 ${contract.contractType === 'CDI' ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    />
                </FormField>
                <FormField label="Date de retour de congés:">
                    <input
                        type="date"
                        name="return_date_leave"
                        value={contract.return_date_leave || ''}
                        onChange={handleChange}
                        required
                        className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                    />
                </FormField>
            </FormSection>

            <FormSection title="Détails du salaire" color="purple">
                <FormField label="Convention:">
                    <Select
                        options={conventions}
                        onChange={handleConventionChange}
                        value={conventions.find(convention => convention.value === contract.convention)}
                        styles={customSelectStyles}
                        className="w-full"
                        required
                    />
                </FormField>
                <FormField label="Catégorie:">
                    <Select
                        options={filteredCategories}
                        onChange={handleCategoryChange}
                        value={categories.find(category => category.value === contract.category)}
                        styles={customSelectStyles}
                        className="w-full"
                        required
                    />
                </FormField>

                <FormField label="Salaire de base:">
                    <AmountInput
                        value={contract.baseSalary || ''}
                        onChange={() => { }} // Read-only, so no-op
                        className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        readOnly
                    />
                </FormField>
                
                <FormField label="Prime de transport :">
                    <AmountInput
                        name="transport_premium"
                        value={contract.transport_premium}
                        onChange={(newValue) => handleMontantChange('transport_premium', newValue)}
                        className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                    />
                </FormField>

                <FormField >
                    <span className="text-sm text-gray-500">L'employé est cadre</span>
                    <input
                        type="checkbox"
                        name="is_executive"
                        checked={contract.is_executive}
                        onChange={(e) => setContract({ ...contract, is_executive: e.target.checked })}
                        className="checkbox "
                    />
                </FormField>

                <FormField >
                    <span className="text-sm text-gray-500">L'employé est Gerant</span>
                    <input
                        type="checkbox"
                        name="manager"
                        checked={contract.manager}
                        onChange={(e) => setContract({ ...contract, manager: e.target.checked })}
                        className="checkbox "
                    />
                </FormField>

                
            </FormSection>

            {/* Section Sursalaire avec calcul à l'envers */}
            {!showReverseCalculation ? (
                <FormField label="Sursalaire:">
                    <div className="flex gap-2 items-center">
                        <AmountInput
                            name="sursalaire"
                            value={contract.sursalaire || ''}
                            onChange={(newValue) => handleMontantChange('sursalaire', newValue)}
                            className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        />
                        <button
                            type="button"
                            onClick={() => setShowReverseCalculation(true)}
                            className="btn btn-outline btn-sm text-xs px-3 py-1"
                            title="Calculer le sursalaire à partir d'un montant net"
                        >
                            Calc. inversé
                        </button>
                    </div>
                </FormField>
            ) : (
                <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex justify-between items-center">
                        <h4 className="font-medium text-gray-700">Calcul à l'envers du sursalaire</h4>
                        <button
                            type="button"
                            onClick={() => {
                                setShowReverseCalculation(false);
                                setCalculatedSursalaire(null);
                                setDesiredNetSalary('');
                            }}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            ✕
                        </button>
                    </div>

                    <FormField label="Salaire net souhaité:">
                        <AmountInput
                            value={desiredNetSalary}
                            onChange={setDesiredNetSalary}
                            className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                            placeholder="Entrez le montant net souhaité"
                        />
                    </FormField>

                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={handleReverseCalculation}
                            disabled={reverseCalculationLoading || !desiredNetSalary}
                            className="btn btn-sm  text-white flex-1 py-2 bg-[--color-primary]"
                        >
                            {reverseCalculationLoading ? 'Calcul en cours...' : 'Lancer le calcul'}
                        </button>
                    </div>

                    {calculatedSursalaire !== null && (
                        <div className="bg-white border border-green-200 rounded-lg p-3">
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-sm text-gray-600">Sursalaire calculé:</p>
                                    <p className="text-lg font-semibold text-green-600">
                                        {new Intl.NumberFormat('fr-FR').format(calculatedSursalaire)} FCFA
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={copyCalculatedSursalaire}
                                    className="btn btn-success btn-sm"
                                >
                                    Copier
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}



            <div className="flex justify-center mt-8">
                <button
                    type="submit"
                    className="bg-[--color-primary] hover:bg-[--bg-hover] rounded-full text-white px-12 py-2 font-medium transition-all duration-200 shadow-sm"
                >
                    {currentContract ? 'Mettre à jour le contrat' : 'Créer le contrat'}
                </button>
            </div>
        </form>
    );
}

export default ContractForm;